import { useState } from "react";
import { ChevronDown, ChevronUp, Pencil } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type {
  AdminAgentFamily,
  AdminAgentInstance,
} from "@/queries/adminAgents";

import { FamilyEditDialog } from "./FamilyEditDialog";
import { InstanceRow } from "./InstanceRow";
import { countEnabledInFamily, formatConfigValue } from "./utils";

interface FamilyCardProps {
  family: AdminAgentFamily;
  instances: AdminAgentInstance[];
}

export function FamilyCard({ family, instances }: FamilyCardProps) {
  const { t } = useTranslation("admin-agents");
  const [editOpen, setEditOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const familyInstances = instances.filter((i) => i.skill === family.skill);
  const enabledCount = countEnabledInFamily(instances, family.skill);

  // Short summary on the card header: cadence + model + family budget.
  const summary = [
    family.defaults["llm_model"] ? `model=${formatConfigValue(family.defaults["llm_model"])}` : null,
    family.defaults["cadence_minutes"]
      ? `cadence=${formatConfigValue(family.defaults["cadence_minutes"])}m`
      : null,
    family.defaults["daily_budget_usd"]
      ? `budget=$${formatConfigValue(family.defaults["daily_budget_usd"])}`
      : null,
  ].filter(Boolean) as string[];

  return (
    <Card data-testid={`family-card-${family.skill}`}>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex flex-col gap-1">
            <CardTitle>
              <code className="font-mono text-base">{family.skill}</code>
            </CardTitle>
            <CardDescription>{family.description}</CardDescription>
            {summary.length > 0 ? (
              <p className="text-xs text-muted-foreground">{summary.join(" · ")}</p>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditOpen(true)}
              data-testid={`family-edit-defaults-${family.skill}`}
            >
              <Pencil className="h-4 w-4" />
              {t("family.editDefaults")}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded((x) => !x)}
              data-testid={`family-toggle-instances-${family.skill}`}
            >
              {expanded ? (
                <>
                  <ChevronUp className="h-4 w-4" />
                  {t("family.hideInstances")}
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4" />
                  {t("family.viewInstances")}
                </>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <div data-testid={`family-count-${family.skill}`}>
            {t("family.instanceCount", {
              enabled: family.enabled_instance_count,
              total: family.instance_count,
            })}
          </div>
          <div className="text-muted-foreground">
            {t("family.totalBudget")}:{" "}
            <span className="font-medium">
              {t("family.totalBudgetValue", { value: family.total_daily_budget_usd })}
            </span>
          </div>
        </div>

        {expanded ? (
          <ul className="mt-2 flex flex-col">
            {familyInstances.map((inst) => (
              <InstanceRow
                key={inst.instance_id}
                instance={inst}
                isLastEnabledOfFamily={enabledCount === 1 && inst.enabled}
              />
            ))}
          </ul>
        ) : null}
      </CardContent>

      <FamilyEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        family={family}
        instances={instances}
      />
    </Card>
  );
}
